import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Card } from '../../components/ui/Card'
import { Badge } from '../../components/ui/Badge'
import { Input } from '../../components/ui/Input'
import { Button } from '../../components/ui/Button'
import { discordApi } from '../../api/discord.api'
import { toast } from '../../store/toastStore'
import { ApiError } from '../../api/client'
import type { DiscordChannelKey } from '../../api/types'

const CHANNEL_LABELS: Record<DiscordChannelKey, { label: string; hint: string }> = {
  TASKS: { label: 'Aufgaben', hint: 'Allgemeiner Aufgaben-Feed (für zukünftige Automationen)' },
  TASKS_IMPORTANT: { label: 'Wichtige Aufgaben', hint: 'Meldung, wenn eine Aufgabe das Label "Wichtig" erhält' },
  PROBLEMS: { label: 'Probleme', hint: 'Meldung bei den Labels "Problematisch", "Blockiert" oder "Bug"' },
  ROADMAP: { label: 'Roadmap', hint: 'Meldung, wenn eine Roadmap-Phase abgeschlossen wird' },
  LOGS: { label: 'Logs', hint: 'Für zukünftige Automationen' },
  REMINDERS: { label: 'Reminder', hint: 'Reminder mit aktivierter Discord-Benachrichtigung' },
  PROJECT_UPDATES: { label: 'Projektupdates', hint: 'Meldung bei Projektstatus-Änderungen' },
  SYSTEM: { label: 'Systemmeldungen', hint: 'Interne Serverfehler (nur für Admins relevant)' },
}

const CHANNEL_KEYS = Object.keys(CHANNEL_LABELS) as DiscordChannelKey[]

function ChannelMappingRow({ channelKey, currentValue }: { channelKey: DiscordChannelKey; currentValue: string }) {
  const queryClient = useQueryClient()
  const [value, setValue] = useState(currentValue)

  const saveMutation = useMutation({
    mutationFn: () => discordApi.setMapping(channelKey, value),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['discord', 'mappings'] })
      toast.success(`${CHANNEL_LABELS[channelKey].label}: Channel gespeichert.`)
    },
    onError: (err) => toast.error(err instanceof ApiError ? err.message : 'Speichern fehlgeschlagen.'),
  })

  const removeMutation = useMutation({
    mutationFn: () => discordApi.removeMapping(channelKey),
    onSuccess: () => {
      setValue('')
      queryClient.invalidateQueries({ queryKey: ['discord', 'mappings'] })
    },
  })

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '180px 1fr auto auto', gap: 'var(--space-2)', alignItems: 'center' }}>
      <div>
        <div style={{ fontSize: 'var(--text-sm)', fontWeight: 600 }}>{CHANNEL_LABELS[channelKey].label}</div>
        <div style={{ fontSize: 'var(--text-xs)', color: 'var(--text-muted)' }}>{CHANNEL_LABELS[channelKey].hint}</div>
      </div>
      <Input placeholder="Discord-Channel-ID…" value={value} onChange={(e) => setValue(e.target.value)} />
      <Button size="sm" disabled={!value.trim()} loading={saveMutation.isPending} onClick={() => saveMutation.mutate()}>
        Speichern
      </Button>
      <Button size="sm" variant="ghost" disabled={!currentValue} onClick={() => removeMutation.mutate()}>
        Entfernen
      </Button>
    </div>
  )
}

export function DiscordSettingsPanel() {
  const statusQuery = useQuery({ queryKey: ['discord', 'status'], queryFn: async () => (await discordApi.status()).data })
  const mappingsQuery = useQuery({ queryKey: ['discord', 'mappings'], queryFn: async () => (await discordApi.listMappings()).data })

  const mappingByKey = new Map(mappingsQuery.data?.map((m) => [m.key, m.channelId]))

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)', maxWidth: 720 }}>
      <Card>
        <h3 style={{ marginTop: 0 }}>Bot-Status</h3>
        {statusQuery.data ? (
          <div style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'center', flexWrap: 'wrap' }}>
            <Badge variant={statusQuery.data.enabled ? (statusQuery.data.connected ? 'success' : 'warning') : 'neutral'} dot>
              {statusQuery.data.enabled ? (statusQuery.data.connected ? 'Verbunden' : 'Konfiguriert, verbindet…') : 'Deaktiviert'}
            </Badge>
            {statusQuery.data.botTag ? <span style={{ fontSize: 'var(--text-sm)', color: 'var(--text-secondary)' }}>{statusQuery.data.botTag}</span> : null}
          </div>
        ) : null}
        {!statusQuery.data?.enabled ? (
          <p style={{ color: 'var(--text-muted)', fontSize: 'var(--text-sm)', marginBottom: 0 }}>
            Kein <code>DISCORD_BOT_TOKEN</code> gesetzt — die Discord-Integration ist deaktiviert. Das restliche Panel
            funktioniert unabhängig davon vollständig. Token als Umgebungsvariable bzw. Pterodactyl-Startup-Variable setzen,
            um sie zu aktivieren.
          </p>
        ) : null}
      </Card>

      <Card>
        <h3 style={{ marginTop: 0 }}>Channel-Zuordnung</h3>
        <p style={{ color: 'var(--text-muted)', fontSize: 'var(--text-sm)' }}>
          Discord-Channel-IDs (Rechtsklick auf einen Channel in Discord → "ID kopieren", Entwicklermodus muss aktiviert sein).
        </p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
          {CHANNEL_KEYS.map((key) => (
            <ChannelMappingRow key={key} channelKey={key} currentValue={mappingByKey.get(key) ?? ''} />
          ))}
        </div>
      </Card>
    </div>
  )
}
